import camelCase from 'lodash/camelCase';
import {
    MATTER_FIELDS,
    type MatterField,
    type MatterFieldIntakeTag,
    type MatterFieldType,
} from '../constants/matterFields';
import {
    APS_PARTY_COUNT_OPTIONS,
    APS_YES_NO_OPTIONS,
    type ApsOcrEnumOption,
    type ApsOcrExtraction,
} from '../types/apsOcr';
import type { QuestionType } from '../types/templateFormFields';

export interface IntakePrefillTag {
    tag: string;
    label: string;
    example: string;
    question: string;
    matterType?: MatterFieldIntakeTag['matterType'];
    field: MatterField;
}

function getIntakeTagConfigs(field: MatterField): MatterFieldIntakeTag[] {
    if (!field.intake) {
        return [];
    }
    return Array.isArray(field.intake) ? field.intake : [field.intake];
}

function getIntakeTags(field: MatterField): IntakePrefillTag[] {
    return getIntakeTagConfigs(field).map((config) => {
        const label = config.label ?? field.label;
        return {
            tag: config.tag ?? field.key,
            label,
            example: config.example,
            question: config.question ?? label,
            matterType: config.matterType,
            field,
        };
    });
}

/** Intake tags in field list order, as offered in the question drawer's prefill tag picker. */
export const INTAKE_FIELD_PREFILL_TAGS: IntakePrefillTag[] = MATTER_FIELDS.flatMap(getIntakeTags);

const INTAKE_PREFILL_TAGS_BY_TAG = new Map(INTAKE_FIELD_PREFILL_TAGS.map((tag) => [tag.tag, tag]));

export function getIntakePrefillTag(tag: string): IntakePrefillTag | undefined {
    return INTAKE_PREFILL_TAGS_BY_TAG.get(tag);
}

export function getIntakePrefillTagLabel(tag: string): string {
    return getIntakePrefillTag(tag)?.label ?? getMatterField(tag)?.label ?? tag;
}

/** Fields kept out of the intake pre-fill, which clients load without signing in. */
export const INTAKE_WITHHELD_FIELD_KEYS = new Set(['retainerAmount']);

/**
 * Tags offered in the question drawer's prefill tag picker, in field list order: a field's
 * intake tags, or its key when it has none.
 */
export const INTAKE_PREFILL_TAG_OPTIONS: string[] = MATTER_FIELDS.flatMap((field) => {
    const intakeTags = getIntakeTags(field);
    if (intakeTags.length > 0) {
        return intakeTags.map(({ tag }) => tag);
    }
    return INTAKE_WITHHELD_FIELD_KEYS.has(field.key) ? [] : [field.key];
});

/** Every name the field goes by: its key, aliases and intake tags. */
export function getMatterFieldNames(field: MatterField): string[] {
    return [
        ...new Set([
            field.key,
            ...(field.aliases ?? []),
            ...getIntakeTags(field).map(({ tag }) => tag),
        ]),
    ];
}

const MATTER_FIELDS_BY_NAME = new Map<string, MatterField>(
    MATTER_FIELDS.flatMap((field) =>
        getMatterFieldNames(field).map((name) => [camelCase(name), field] as const),
    ),
);

/** The field a name refers to, in any case (`closing_date`, `closingDate`) or by any alias. */
export function getMatterField(name: string): MatterField | undefined {
    return MATTER_FIELDS_BY_NAME.get(camelCase(name));
}

/** The APS extraction key holding this field, or null when the scan does not extract it. */
export function getMatterFieldApsKey(field: MatterField): keyof ApsOcrExtraction | null {
    if (!field.aps) {
        return null;
    }
    return (field.aps.key ?? field.key) as keyof ApsOcrExtraction;
}

export const APS_MATTER_FIELDS = MATTER_FIELDS.filter((field) => field.aps);

export function getMatterFieldOptions(field: MatterField): ApsOcrEnumOption[] | undefined {
    if (field.type === 'yesNo') {
        return APS_YES_NO_OPTIONS;
    }
    if (field.type === 'count') {
        return APS_PARTY_COUNT_OPTIONS;
    }
    return field.options;
}

export type MatterFieldWidget = 'address' | 'date' | 'currency' | 'text';

const WIDGET_BY_FIELD_TYPE: Partial<Record<MatterFieldType, MatterFieldWidget>> = {
    address: 'address',
    date: 'date',
    currency: 'currency',
};

/** The input a field is edited with when it has no options. */
export function getMatterFieldWidget(field: MatterField): MatterFieldWidget {
    return WIDGET_BY_FIELD_TYPE[field.type] ?? 'text';
}

const QUESTION_TYPE_BY_FIELD_TYPE: Record<MatterFieldType, QuestionType> = {
    text: 'text',
    textarea: 'textarea',
    address: 'address',
    date: 'date',
    currency: 'currency',
    yesNo: 'radio',
    count: 'radio',
    select: 'select',
    list: 'text',
};

export function getMatterFieldQuestionType(field: MatterField): QuestionType {
    return QUESTION_TYPE_BY_FIELD_TYPE[field.type];
}

export function isIntakeTagForMatterType(tag: IntakePrefillTag, matterType: string): boolean {
    if (!tag.matterType || (matterType !== 'Purchase' && matterType !== 'Sale')) {
        return true;
    }
    return tag.matterType === matterType;
}

/** Intake tags that answer the field on the matter type, in field list order. */
export function getMatterFieldIntakeTags(field: MatterField, matterType: string): string[] {
    return getIntakeTags(field)
        .filter((intakeTag) => isIntakeTagForMatterType(intakeTag, matterType))
        .map(({ tag }) => tag);
}

/** The value an intake question of this field accepts, or null when it would not fit. */
function formatIntakePrefillValue(field: MatterField, value: string): string | null {
    const trimmed = field.type === 'currency' ? value.replace(/[$,]/g, '').trim() : value.trim();
    if (!trimmed) {
        return null;
    }

    const options = getMatterFieldOptions(field);
    if (options && !options.some((option) => option.value === trimmed)) {
        return null;
    }

    return trimmed;
}

/**
 * Intake prefill values keyed by prefill tag, from values keyed by any field name. Fields with
 * intake tags are written to the tags for the matter type, other fields to their key, and names
 * outside the field list are passed through in camelCase. Withheld fields are left out.
 */
export function mapFieldValuesToIntakePrefill(
    values: Record<string, string | number | null | undefined>,
    matterType: string,
): Record<string, string> {
    const prefill: Record<string, string> = {};

    for (const [name, raw] of Object.entries(values)) {
        const value = raw == null ? '' : String(raw).trim();
        if (!value) {
            continue;
        }

        const field = getMatterField(name);
        if (!field) {
            prefill[camelCase(name)] = value;
            continue;
        }
        if (INTAKE_WITHHELD_FIELD_KEYS.has(field.key)) {
            continue;
        }

        const intakeTags = getIntakeTags(field);
        if (intakeTags.length === 0) {
            prefill[field.key] = value;
            continue;
        }

        const formatted = formatIntakePrefillValue(field, value);
        if (!formatted) {
            continue;
        }
        for (const intakeTag of intakeTags) {
            if (isIntakeTagForMatterType(intakeTag, matterType)) {
                prefill[intakeTag.tag] = formatted;
            }
        }
    }

    return prefill;
}
